// src/redux/slices/agentsSlice.js
// Redux slice for the real agentic layer agents (distinct from prompts)

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// Fetch all agents
export const fetchAgents = createAsyncThunk(
  'agents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agents-list', {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Save agent customization
export const saveAgent = createAsyncThunk(
  'agents/save',
  async (agentData, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agents-save', {
        method: 'POST',
        body: JSON.stringify(agentData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return rejectWithValue(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const agentsSlice = createSlice({
  name: 'agents',
  initialState: {
    items: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearAgentsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.agents || [];
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch agents';
      })
      .addCase(saveAgent.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveAgent.fulfilled, (state, action) => {
        state.saving = false;
        const saved = action.payload.agent;
        if (saved) {
          const idx = state.items.findIndex(a => a.agent_key === saved.agent_key);
          if (idx >= 0) {
            state.items[idx] = saved;
          } else {
            state.items.push(saved);
          }
        }
      })
      .addCase(saveAgent.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to save agent';
      });
  },
});

export const { clearAgentsError } = agentsSlice.actions;

// Selectors
export const selectAllAgents = (state) => state.agents?.items || [];
export const selectBuilderAgents = (state) => (state.agents?.items || []).filter(a => a.agent_category === 'builder');
export const selectOrchestratorAgent = (state) => (state.agents?.items || []).find(a => a.agent_category === 'orchestrator');
export const selectAgentsLoading = (state) => state.agents?.loading || false;
export const selectAgentsSaving = (state) => state.agents?.saving || false;
export const selectAgentsError = (state) => state.agents?.error || null;

export default agentsSlice.reducer;
